import { Add } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { GridApi, GridRowId } from '@mui/x-data-grid-pro';
import { MutableRefObject, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  LineItemDefaultValues,
  useIntakePurchaseRequisitionGridConfig,
  useLineItemsGridHandlers,
} from '../../IntakeRequestLineItemStep.dataGrid';
import { IntakeRequestLineItem, intakeRequestLineItemArraySchema } from '../../IntakeRequestLineItemStep.schema';
import { IntakeRequestStepLineItemGridField } from '../../IntakeRequestLineItemStep.types';
import { IntakeRequestStepLineItemForm } from '../IntakeRequestStepLineItemForm/IntakeRequestStepLineItemForm';
import { I18nNamespace } from '~/common/types';
import { GridRowClickAwayListener } from '~/routes/IntakeDetail/components/GridRowClickAwayListener/GridRowClickAwayListener';
import { IntakeDataGrid } from '~/routes/IntakeDetail/components/IntakeDataGrid/IntakeDataGrid';
import { useGridValidationStyling } from '~/routes/IntakeDetail/components/IntakeDataGrid/IntakeDataGrid.hooks';
import { IntakeDrawer } from '~/routes/IntakeDetail/components/IntakeDrawer/IntakeDrawer';
import { IntakeDrawerContent } from '~/routes/IntakeDetail/components/IntakeDrawer/IntakeDrawerContent';
import { IntakeDrawerTitle } from '~/routes/IntakeDetail/components/IntakeDrawer/IntakeDrawerTitle';

type IntakeRequestStepLineItemsProps = {
  disabled?: boolean;
  apiRef: MutableRefObject<GridApi>;
  lineItems: IntakeRequestLineItem[];
  defaultValues: LineItemDefaultValues;
};

export const IntakeRequestStepLineItems = ({
  disabled,
  apiRef,
  lineItems,
  defaultValues,
}: IntakeRequestStepLineItemsProps) => {
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: 'IntakeRequestLineItemStepForm' });

  const getRowIndex = useCallback(
    (rowId: GridRowId) => {
      const rowIds = apiRef.current.getAllRowIds();
      const rowIndex = rowIds?.findIndex((rId) => rId === rowId);
      return rowIndex;
    },
    [apiRef],
  );

  const { getRowClassName, getGridClassName } = useGridValidationStyling(apiRef, intakeRequestLineItemArraySchema);

  const {
    validate,
    currentSelectedRowId,
    setRowSelectionModel,
    selectedLineItem,
    handleLineItemChange,
    handleAddRow,
    handleRowDelete,
    handleCloseDrawer,
  } = useLineItemsGridHandlers(apiRef, defaultValues);

  const config = useIntakePurchaseRequisitionGridConfig({ getRowIndex, onRowDelete: handleRowDelete, disabled });

  // enforce initial selection state and avoid error coloring
  // temporary fix until proper table validation styling is in place
  useEffect(() => {
    setRowSelectionModel([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItems]);

  const rowSelectionModel = currentSelectedRowId ? [currentSelectedRowId] : [];

  return (
    <>
      <GridRowClickAwayListener onClickAway={handleCloseDrawer}>
        <IntakeDataGrid
          {...config}
          apiRef={apiRef}
          rows={lineItems}
          getRowHeight={() => 'auto'}
          initialState={{
            pinnedColumns: { right: [IntakeRequestStepLineItemGridField.DELETE_ACTION] },
          }}
          editMode="row"
          columns={config.columns}
          onRowSelectionModelChange={setRowSelectionModel}
          rowSelectionModel={rowSelectionModel}
          sortingMode={'server'}
          getRowClassName={({ id, row }) => {
            return id !== currentSelectedRowId || validate ? getRowClassName(row) : '';
          }}
          className={getGridClassName()}
        />
        <IntakeDrawer isOpen={!!selectedLineItem} handleCloseDrawer={handleCloseDrawer}>
          <IntakeDrawerTitle title={t('header')} handleCloseDrawer={handleCloseDrawer} />
          <IntakeDrawerContent>
            {selectedLineItem && (
              <IntakeRequestStepLineItemForm
                key={selectedLineItem.id}
                lineItem={selectedLineItem}
                validate={validate}
                onChange={handleLineItemChange}
                disabled={disabled}
              />
            )}
          </IntakeDrawerContent>
        </IntakeDrawer>
      </GridRowClickAwayListener>
      <Box
        sx={{
          mt: 3,
        }}
      >
        <Button
          disabled={disabled}
          variant="text"
          onClick={(e) => {
            e.stopPropagation();
            handleAddRow();
          }}
          startIcon={<Add />}
          dataintercom-target="IntakePurchaseRequisition/LineItems/Add"
        >
          {t('addLineItemButton')}
        </Button>
      </Box>
    </>
  );
};
